import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { countryName } from "@/lib/locations";

// Fix default Leaflet marker icons (shadowless black/lime themed marker)
const customIcon = new L.DivIcon({
  html: `<div style="width:28px;height:28px;background:#CCFF00;border:2px solid #0A0A0A;border-radius:50%;box-shadow:0 0 0 2px rgba(204,255,0,0.4),0 4px 12px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;color:#0A0A0A;font-weight:900;font-family:system-ui;font-size:14px;">P</div>`,
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const CITY_COORDS = {
  // Mexico City fallback for clubs without lat/lng
  MX: [19.4326, -99.1332],
  AR: [-34.6037, -58.3816],
  ES: [40.4168, -3.7038],
  US: [38.9072, -77.0369],
  CL: [-33.4489, -70.6693],
  CO: [4.711, -74.0721],
  PE: [-12.0464, -77.0428],
  UY: [-34.9011, -56.1645],
  BR: [-14.235, -51.9253],
};

/**
 * Displays clubs with lat/lng on an interactive map.
 * @param clubs - array of club objects with optional `latitude` / `longitude`
 */
export default function ClubsMap({ clubs }) {
  const withCoords = useMemo(() => clubs.filter((c) => c.latitude != null && c.longitude != null), [clubs]);

  const center = useMemo(() => {
    if (withCoords.length > 0) {
      const avgLat = withCoords.reduce((s, c) => s + c.latitude, 0) / withCoords.length;
      const avgLng = withCoords.reduce((s, c) => s + c.longitude, 0) / withCoords.length;
      return [avgLat, avgLng];
    }
    // Fallback: most common country in dataset
    const byC = {};
    clubs.forEach((c) => {
      const cc = (c.country || "MX").toUpperCase();
      byC[cc] = (byC[cc] || 0) + 1;
    });
    const top = Object.entries(byC).sort(([, a], [, b]) => b - a)[0]?.[0] || "MX";
    return CITY_COORDS[top] || CITY_COORDS.MX;
  }, [clubs, withCoords]);

  if (clubs.length === 0) {
    return (
      <div className="card-tactical p-8 text-center">
        <p className="text-gray-400">No hay clubes para mostrar en el mapa</p>
      </div>
    );
  }

  return (
    <div className="card-tactical overflow-hidden" data-testid="clubs-map-container">
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Ubicacion</p>
          <p className="text-sm text-white font-semibold">{withCoords.length} de {clubs.length} clubes en el mapa</p>
        </div>
        {withCoords.length < clubs.length && (
          <p className="text-[11px] text-orange-400">
            Tip: Edita cada club y anade latitud/longitud para que aparezca en el mapa.
          </p>
        )}
      </div>
      <MapContainer center={center} zoom={withCoords.length > 0 ? 6 : 4} style={{ height: 480, width: "100%", background: "#0A0A0A" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {withCoords.map((club) => (
          <Marker key={club.id} position={[club.latitude, club.longitude]} icon={customIcon}>
            <Popup>
              <div style={{ fontFamily: "system-ui", minWidth: 200 }}>
                <p style={{ fontWeight: 900, fontSize: 14, margin: 0 }}>{club.name}</p>
                <p style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                  {countryName(club.country || "MX")}{club.state ? ` · ${club.state}` : ""}{club.city ? ` · ${club.city}` : ""}
                </p>
                {club.address && <p style={{ fontSize: 11, color: "#444", marginTop: 4 }}>{club.address}</p>}
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  <Link
                    to={`/dashboard/clubes/${club.id}`}
                    style={{ padding: "4px 10px", background: "#CCFF00", color: "#000", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", textDecoration: "none" }}
                  >
                    Ver club
                  </Link>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${club.latitude},${club.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: "4px 10px", background: "#4285F4", color: "#fff", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", textDecoration: "none" }}
                    data-testid={`gmaps-${club.id}`}
                  >
                    Google Maps
                  </a>
                  <a
                    href={`https://waze.com/ul?ll=${club.latitude},${club.longitude}&navigate=yes`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: "4px 10px", background: "#33CCFF", color: "#000", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", textDecoration: "none" }}
                    data-testid={`waze-${club.id}`}
                  >
                    Waze
                  </a>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
